import type { FloatingNavMode } from '@/store/ui-preferences.store'
import type { CSSProperties, ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { useUIPreferencesStore } from '@/store/ui-preferences.store'
import {
  ArrowsLeftRightIcon,
  CheckIcon,
  DotsThreeOutlineVerticalIcon,
  SidebarSimpleIcon,
} from '@phosphor-icons/react'

import { SidebarPresetMenu } from './SidebarPresetMenu'

const MODES = [
  {
    value: 'open',
    label: 'Open',
    description: 'Always show labels',
    icon: SidebarSimpleIcon,
  },
  {
    value: 'closed',
    label: 'Closed',
    description: 'Keep icons only',
    icon: DotsThreeOutlineVerticalIcon,
  },
  {
    value: 'hybrid',
    label: 'Hybrid',
    description: 'A mix of Open and Closed',
    icon: ArrowsLeftRightIcon,
  },
] as const

export function FloatingNavModeMenu({ className }: { className?: string }) {
  const mode = useUIPreferencesStore(state => state.floatingNavMode)
  const setMode = useUIPreferencesStore(state => state.setFloatingNavMode)
  return (
    <NavigationViewMenu
      mode={mode}
      onModeChange={setMode}
      className={className}
    >
      <SidebarPresetMenu />
    </NavigationViewMenu>
  )
}

/** Shared chooser for the workspace and separately branded client portal. */
export function NavigationViewMenu({
  mode,
  onModeChange,
  className,
  portal = false,
  contentStyle,
  children,
}: {
  mode: FloatingNavMode
  onModeChange: (mode: FloatingNavMode) => void
  className?: string
  portal?: boolean
  contentStyle?: CSSProperties
  children?: ReactNode
}) {
  const selected = MODES.find(option => option.value === mode)!

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          data-sidebar="trigger"
          variant="ghost"
          size="icon"
          className={cn('h-8 w-8', className)}
          aria-label={`Navigation view: ${selected.label}`}
          title={`Navigation view: ${selected.label}`}
        >
          <SidebarSimpleIcon className="h-[18px] w-[18px]" weight="bold" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={8}
        data-nav-style="floating"
        data-portal-nav-menu={portal || undefined}
        style={contentStyle}
        className="floating-nav-mode-menu w-64 p-2"
      >
        <DropdownMenuLabel className="flex items-center gap-2 px-3 pb-2 pt-1.5 text-[11px] uppercase tracking-wider text-[hsl(var(--sidebar-text-muted))]">
          <span
            aria-hidden
            className="h-1.5 w-1.5 rounded-full bg-[var(--icon-accent)]"
          />
          Navigation view
        </DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={mode}
          className="space-y-1"
          onValueChange={value => {
            if (value === 'open' || value === 'closed' || value === 'hybrid')
              onModeChange(value)
          }}
        >
          {MODES.map(option => (
            <DropdownMenuRadioItem
              key={option.value}
              value={option.value}
              className="gap-3 rounded-xl px-3 py-2.5 text-[hsl(var(--sidebar-text))] hover:bg-[hsl(var(--sidebar-text)/0.1)] hover:text-[hsl(var(--sidebar-text))] focus:bg-[hsl(var(--sidebar-text)/0.1)] data-[state=checked]:bg-[hsl(var(--sidebar-text)/0.18)] [&>span:first-child]:hidden"
            >
              <option.icon
                aria-hidden
                className="h-5 w-5 shrink-0 text-[var(--icon-accent)]"
                weight="duotone"
              />
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="text-sm font-medium">{option.label}</span>
                <span className="text-xs text-[hsl(var(--sidebar-text-muted))]">
                  {option.description}
                </span>
              </span>
              <CheckIcon
                aria-hidden
                className={cn(
                  'h-4 w-4 shrink-0',
                  mode !== option.value && 'invisible',
                )}
                weight="bold"
              />
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
